import { ActiveMission } from "../systems/Missions";
import { CARS, CarDef } from "../content/cars";
import { BRAND, tierFor, dspIcons } from "../content/brand";
import { Storage } from "../systems/Storage";
import { PowerUpSystem } from "../game/PowerUps";

/**
 * Builds and updates the DOM HUD/menu/game-over overlays. Pure DOM,
 * positioned over the WebGL canvas. Pointer-events: none on decorative
 * containers so they don't eat swipes; explicit pointer-events: auto
 * on buttons.
 */
export class HUD {
  private root: HTMLElement;
  // In-run HUD
  private hud: HTMLDivElement;
  private elScore!: HTMLSpanElement;
  private elDist!: HTMLSpanElement;
  private elCoins!: HTMLSpanElement;
  private elMult!: HTMLSpanElement;
  private elPause!: HTMLButtonElement;
  private puMagnet!: HTMLDivElement;
  private puShield!: HTMLDivElement;
  private puNitro!: HTMLDivElement;
  private puX2!: HTMLDivElement;
  private toast!: HTMLDivElement;
  // Route progress bar
  private elRouteFill!: HTMLDivElement;
  private elRouteMilestones!: HTMLDivElement[];

  // Menu
  private menu: HTMLDivElement;
  private elMenuMissions!: HTMLDivElement;
  private elMenuShop!: HTMLDivElement;
  private elMenuCoins!: HTMLSpanElement;
  private elMenuBest!: HTMLSpanElement;
  private elMenuAccount!: HTMLParagraphElement;
  private elMenuEmail!: HTMLSpanElement;
  private elMenuGuest!: HTMLParagraphElement;
  private elMenuTier!: HTMLSpanElement;

  // Pause overlay
  private pause: HTMLDivElement;

  // Game over
  private go: HTMLDivElement;
  private elGoScore!: HTMLSpanElement;
  private elGoCoins!: HTMLSpanElement;
  private elGoDist!: HTMLSpanElement;
  private elGoNewBest!: HTMLDivElement;

  // Victory
  private victory: HTMLDivElement;
  private elVicScore!: HTMLSpanElement;
  private elVicCoins!: HTMLSpanElement;
  private elVicNewBest!: HTMLDivElement;

  // Install hint
  private installHint!: HTMLDivElement;

  constructor(parent: HTMLElement, private storage: Storage) {
    this.root = parent;
    this.hud = this.buildHud();
    this.menu = this.buildMenu();
    this.pause = this.buildPause();
    this.go = this.buildGameOver();
    this.victory = this.buildVictory();
    this.installHint = this.buildInstallHint();
    parent.appendChild(this.hud);
    parent.appendChild(this.menu);
    parent.appendChild(this.pause);
    parent.appendChild(this.go);
    parent.appendChild(this.victory);
    parent.appendChild(this.installHint);
  }

  // ---- Public API ----

  showMenu(missions: ActiveMission[], missionLabels: (m: ActiveMission) => string): void {
    this.hud.classList.add("hidden");
    this.pause.classList.add("hidden");
    this.go.classList.add("hidden");
    this.victory.classList.add("hidden");
    this.menu.classList.remove("hidden");
    this.renderMenuMissions(missions, missionLabels);
    this.renderShop();
    this.refreshMenuStats();
  }

  showHud(): void {
    this.menu.classList.add("hidden");
    this.pause.classList.add("hidden");
    this.go.classList.add("hidden");
    this.victory.classList.add("hidden");
    this.hud.classList.remove("hidden");
  }

  showPause(): void {
    this.pause.classList.remove("hidden");
  }
  hidePause(): void {
    this.pause.classList.add("hidden");
  }

  showGameOver(opts: {
    score: number;
    coins: number;
    distance: number;
    newBest: boolean;
  }): void {
    this.hud.classList.add("hidden");
    this.victory.classList.add("hidden");
    this.go.classList.remove("hidden");
    this.elGoScore.textContent = String(opts.score);
    this.elGoCoins.textContent = String(opts.coins);
    this.elGoDist.textContent = String(Math.floor(opts.distance)) + "m";
    this.elGoNewBest.style.display = opts.newBest ? "block" : "none";
  }

  showVictory(opts: { score: number; coins: number; newBest: boolean }): void {
    this.hud.classList.add("hidden");
    this.go.classList.add("hidden");
    this.victory.classList.remove("hidden");
    this.elVicScore.textContent = String(opts.score);
    this.elVicCoins.textContent = String(opts.coins);
    this.elVicNewBest.style.display = opts.newBest ? "block" : "none";
  }

  setProgress(ratio: number): void {
    const pct = Math.min(1, Math.max(0, ratio)) * 100;
    this.elRouteFill.style.width = pct.toFixed(1) + "%";
    const thresholds = [0.25, 0.5, 0.75];
    thresholds.forEach((t, i) => {
      this.elRouteMilestones[i].classList.toggle("passed", ratio >= t);
    });
  }

  setStats(score: number, distance: number, coins: number, multiplier: number): void {
    this.elScore.textContent = String(score);
    this.elDist.textContent = `${Math.floor(distance)}m`;
    this.elCoins.textContent = String(coins);
    this.elMult.textContent = `×${multiplier.toFixed(0)}`;
    this.elMult.style.display = multiplier > 1 ? "" : "none";
  }

  setPowerUps(p: PowerUpSystem): void {
    this.setPu(this.puMagnet, p.hasMagnet(), p.remaining("magnet"));
    this.setPu(this.puShield, p.hasShield(), p.hasShield() ? 1 : 0);
    this.setPu(this.puNitro, p.hasNitro(), p.remaining("nitro"));
    this.setPu(this.puX2, p.hasX2(), p.remaining("x2"));
  }

  showToast(text: string, ms = 1400): void {
    this.toast.textContent = text;
    this.toast.classList.add("show");
    window.clearTimeout((this.toast as any).__t);
    (this.toast as any).__t = window.setTimeout(() => {
      this.toast.classList.remove("show");
    }, ms);
  }

  showInstallHint(): void {
    if (sessionStorage.getItem("installHintDismissed")) return;
    this.installHint.classList.add("show");
  }

  // ---- Wiring (callbacks the Game class will set) ----
  onPlay: () => void = () => {};
  onPause: () => void = () => {};
  onResume: () => void = () => {};
  onRestart: () => void = () => {};
  onMenu: () => void = () => {};
  onMute: (muted: boolean) => void = () => {};
  onShare: () => void = () => {};
  onSelectCar: (car: CarDef) => void = () => {};
  onBuyCar: (car: CarDef) => boolean = () => false;
  onSignOut: () => void = () => {};
  refreshMissionsCb: () => ActiveMission[] = () => [];
  missionLabelCb: (m: ActiveMission) => string = (m) => m.id;
  accountEmailCb: () => string | null = () => null;
  accountIsGuestCb: () => boolean = () => false;

  // ---- Build helpers ----

  private buildHud(): HTMLDivElement {
    const root = document.createElement("div");
    root.className = "screen hidden";
    root.innerHTML = `
      <div>
        <div class="route-wrap">
          <div class="route-track">
            <div class="route-fill" id="route-fill"></div>
            <div class="route-ms" id="ms-0" style="left:25%"></div>
            <div class="route-ms" id="ms-1" style="left:50%"></div>
            <div class="route-ms" id="ms-2" style="left:75%"></div>
          </div>
          <div class="route-labels">
            <span>LA</span>
            <span>K-Town ★</span>
          </div>
        </div>
        <div class="hud-top">
          <div class="stack">
            <span class="chip"><span class="label">Coins</span><span class="value" id="hud-coins">0</span></span>
          </div>
          <div>
            <div class="score-big" id="hud-score">0</div>
            <div class="dist" id="hud-dist">0m</div>
            <div style="text-align:center;margin-top:4px;"><span class="chip" id="hud-mult" style="display:none">×1</span></div>
          </div>
          <div class="stack right">
            <button class="pause-btn" id="hud-pause" aria-label="Pause">II</button>
          </div>
        </div>
        <div class="powerups">
          <div class="pu magnet off" id="pu-magnet"><div class="ring"></div>🧲</div>
          <div class="pu off" id="pu-shield"><div class="ring"></div>🛡</div>
          <div class="pu nitro off" id="pu-nitro" style="display:none"><div class="ring"></div>🔥</div>
          <div class="pu x2 off" id="pu-x2"><div class="ring"></div>×2</div>
        </div>
      </div>
      <div></div>
      <div class="toast" id="toast"></div>
    `;
    this.elScore = root.querySelector("#hud-score") as HTMLSpanElement;
    this.elDist = root.querySelector("#hud-dist") as HTMLSpanElement;
    this.elCoins = root.querySelector("#hud-coins") as HTMLSpanElement;
    this.elMult = root.querySelector("#hud-mult") as HTMLSpanElement;
    this.elPause = root.querySelector("#hud-pause") as HTMLButtonElement;
    this.puMagnet = root.querySelector("#pu-magnet") as HTMLDivElement;
    this.puShield = root.querySelector("#pu-shield") as HTMLDivElement;
    this.puNitro = root.querySelector("#pu-nitro") as HTMLDivElement;
    this.puX2 = root.querySelector("#pu-x2") as HTMLDivElement;
    this.toast = root.querySelector("#toast") as HTMLDivElement;
    this.elRouteFill = root.querySelector("#route-fill") as HTMLDivElement;
    this.elRouteMilestones = [
      root.querySelector("#ms-0") as HTMLDivElement,
      root.querySelector("#ms-1") as HTMLDivElement,
      root.querySelector("#ms-2") as HTMLDivElement,
    ];
    this.elPause.addEventListener("click", () => this.onPause());
    return root;
  }

  private buildVictory(): HTMLDivElement {
    const root = document.createElement("div");
    root.className = "screen modal hidden victory-screen";
    root.innerHTML = `
      <div></div>
      <div class="title">
        <div class="vic-trophy">🏆</div>
        <h1 class="vic-heading">K-Town!</h1>
        <h2>You made it to the show</h2>
        <div class="new-best" id="vic-newbest" style="display:none">⭐ New Record ⭐</div>
        <div class="go-stats">
          <div class="stat"><div class="v" id="vic-score">0</div><div class="l">Score</div></div>
          <div class="stat vic-dist-stat"><div class="v">2000m</div><div class="l">Completed ✓</div></div>
          <div class="stat"><div class="v" id="vic-coins">0</div><div class="l">Coins</div></div>
        </div>
      </div>
      <div class="btn-row">
        <button class="btn gold" id="vic-play">Run It Again</button>
        <button class="btn secondary" id="vic-share">Share Score</button>
        <button class="btn secondary" id="vic-menu">Menu</button>
      </div>
      ${dspIconsHtml()}
    `;
    this.elVicScore   = root.querySelector("#vic-score")   as HTMLSpanElement;
    this.elVicCoins   = root.querySelector("#vic-coins")   as HTMLSpanElement;
    this.elVicNewBest = root.querySelector("#vic-newbest") as HTMLDivElement;
    (root.querySelector("#vic-play")  as HTMLButtonElement).addEventListener("click", () => this.onRestart());
    (root.querySelector("#vic-share") as HTMLButtonElement).addEventListener("click", () => this.onShare());
    (root.querySelector("#vic-menu")  as HTMLButtonElement).addEventListener("click", () => this.onMenu());
    return root;
  }

  private buildMenu(): HTMLDivElement {
    const root = document.createElement("div");
    root.className = "screen modal";
    root.innerHTML = `
      <div class="hud-top">
        <div class="stack"><span class="chip"><span class="label">Coins</span><span class="value" id="menu-coins">0</span></span></div>
        <div></div>
        <div class="stack right">
          <button class="muted-toggle" id="menu-mute">🔊</button>
        </div>
      </div>
      <div class="title">
        <h1>${BRAND.artistHtml}</h1>
        <h2>${escapeHtml(BRAND.tagline)}</h2>
        <p class="tag">You're late to your show. Race the supercar through downtown to K-town. Tap to start. Swipe to switch lanes, up to jump, down to slide.</p>
        <p class="tag" style="margin-top:8px"><span style="color:var(--gold)">Best:</span> <span id="menu-best">0</span> <span class="tier-chip" id="menu-tier" style="display:none"></span></p>
        <p class="signed-in" id="menu-account" style="display:none">
          <span class="email" id="menu-email"></span><a id="menu-signout">Sign out</a>
        </p>
        <p class="signed-in" id="menu-guest" style="display:none">
          <span class="email">Playing as Guest</span><a id="menu-signin">Sign in to save</a>
        </p>
      </div>
      <div>
        <div class="menu-grid">
          <div class="missions">
            <h3>Daily Missions</h3>
            <div id="menu-missions"></div>
          </div>
          <div class="shop">
            <h3>Garage</h3>
            <div id="menu-shop"></div>
          </div>
        </div>
        <div class="btn-row">
          <button class="btn" id="menu-play">Drive</button>
        </div>
        ${dspIconsHtml()}
      </div>
    `;
    this.elMenuMissions = root.querySelector("#menu-missions") as HTMLDivElement;
    this.elMenuShop = root.querySelector("#menu-shop") as HTMLDivElement;
    this.elMenuCoins = root.querySelector("#menu-coins") as HTMLSpanElement;
    this.elMenuBest = root.querySelector("#menu-best") as HTMLSpanElement;
    this.elMenuAccount = root.querySelector("#menu-account") as HTMLParagraphElement;
    this.elMenuEmail = root.querySelector("#menu-email") as HTMLSpanElement;
    this.elMenuGuest = root.querySelector("#menu-guest") as HTMLParagraphElement;
    this.elMenuTier = root.querySelector("#menu-tier") as HTMLSpanElement;
    (root.querySelector("#menu-signout") as HTMLAnchorElement).addEventListener("click", (e) => {
      e.preventDefault();
      this.onSignOut();
    });
    // "Sign in to save" from guest mode reuses the same teardown as sign-out
    // (clears guest flag, local data, and re-shows the auth screen).
    (root.querySelector("#menu-signin") as HTMLAnchorElement).addEventListener("click", (e) => {
      e.preventDefault();
      this.onSignOut();
    });
    (root.querySelector("#menu-play") as HTMLButtonElement).addEventListener("click", () => this.onPlay());
    const mute = root.querySelector("#menu-mute") as HTMLButtonElement;
    mute.addEventListener("click", () => {
      const next = !mute.classList.contains("muted");
      mute.classList.toggle("muted", next);
      mute.textContent = next ? "🔇" : "🔊";
      this.onMute(next);
    });
    return root;
  }

  private buildPause(): HTMLDivElement {
    const root = document.createElement("div");
    root.className = "screen modal hidden";
    root.innerHTML = `
      <div></div>
      <div class="title">
        <h1 style="font-size:48px">Paused</h1>
      </div>
      <div class="btn-row">
        <button class="btn" id="p-resume">Resume</button>
        <button class="btn secondary" id="p-restart">Restart</button>
        <button class="btn secondary" id="p-menu">Menu</button>
      </div>
    `;
    (root.querySelector("#p-resume") as HTMLButtonElement).addEventListener("click", () => this.onResume());
    (root.querySelector("#p-restart") as HTMLButtonElement).addEventListener("click", () => this.onRestart());
    (root.querySelector("#p-menu") as HTMLButtonElement).addEventListener("click", () => this.onMenu());
    return root;
  }

  private buildGameOver(): HTMLDivElement {
    const root = document.createElement("div");
    root.className = "screen modal hidden";
    root.innerHTML = `
      <div></div>
      <div class="title">
        <h1 style="font-size:56px">Wrap!</h1>
        <h2>Show's not over yet</h2>
        <div class="new-best" id="go-newbest" style="display:none">⭐ New Best ⭐</div>
        <div class="go-stats">
          <div class="stat"><div class="v" id="go-score">0</div><div class="l">Score</div></div>
          <div class="stat"><div class="v" id="go-dist">0m</div><div class="l">Distance</div></div>
          <div class="stat"><div class="v" id="go-coins">0</div><div class="l">Coins</div></div>
        </div>
      </div>
      <div class="btn-row">
        <button class="btn" id="go-retry">Run It Back</button>
        <button class="btn secondary" id="go-share">Share Score</button>
        <button class="btn secondary" id="go-menu">Menu</button>
      </div>
      ${dspIconsHtml()}
    `;
    this.elGoScore = root.querySelector("#go-score") as HTMLSpanElement;
    this.elGoDist = root.querySelector("#go-dist") as HTMLSpanElement;
    this.elGoCoins = root.querySelector("#go-coins") as HTMLSpanElement;
    this.elGoNewBest = root.querySelector("#go-newbest") as HTMLDivElement;
    (root.querySelector("#go-retry") as HTMLButtonElement).addEventListener("click", () => this.onRestart());
    (root.querySelector("#go-share") as HTMLButtonElement).addEventListener("click", () => this.onShare());
    (root.querySelector("#go-menu") as HTMLButtonElement).addEventListener("click", () => this.onMenu());
    return root;
  }

  private buildInstallHint(): HTMLDivElement {
    const root = document.createElement("div");
    root.className = "install-hint";
    root.innerHTML = `
      <button class="close" aria-label="Close">×</button>
      Add to Home Screen for fullscreen play: tap the Share icon, then "Add to Home Screen".
    `;
    (root.querySelector(".close") as HTMLButtonElement).addEventListener("click", () => {
      sessionStorage.setItem("installHintDismissed", "1");
      root.classList.remove("show");
    });
    return root;
  }

  private setPu(el: HTMLElement, on: boolean, p: number): void {
    el.classList.toggle("off", !on);
    el.style.setProperty("--p", p.toString());
  }

  private renderMenuMissions(missions: ActiveMission[], label: (m: ActiveMission) => string): void {
    this.elMenuMissions.innerHTML = "";
    if (missions.length === 0) {
      this.elMenuMissions.textContent = "No missions today.";
      return;
    }
    for (const m of missions) {
      const row = document.createElement("div");
      row.className = "mission" + (m.done ? " done" : "");
      const pct = Math.min(100, Math.round((m.progress / m.goal) * 100));
      row.innerHTML = `
        <div class="text">
          ${escapeHtml(label(m))}
          <div class="bar"><div style="width:${pct}%"></div></div>
          <div style="font-size:11px;color:var(--ink-dim);margin-top:2px">${m.progress}/${m.goal}</div>
        </div>
        <div class="reward">+${m.reward}</div>
      `;
      this.elMenuMissions.appendChild(row);
    }
  }

  private renderShop(): void {
    this.elMenuShop.innerHTML = "";
    const data = this.storage.get();
    for (const car of CARS) {
      const owned = data.ownedSkins.includes(car.id);
      const equipped = data.equippedSkin === car.id;
      const row = document.createElement("div");
      row.className = "car-row";
      const swatch = carSwatchHtml(car);
      const sub = owned ? "Owned" : `${car.price} coins`;
      let btnHtml = "";
      if (equipped) btnHtml = `<button class="action equipped" disabled>Equipped</button>`;
      else if (owned) btnHtml = `<button class="action owned" data-act="equip">Equip</button>`;
      else btnHtml = `<button class="action locked" data-act="buy">Buy</button>`;
      row.innerHTML = `
        ${swatch}
        <div class="car-info">
          <div class="name">${escapeHtml(car.name)}</div>
          <div class="sub">${sub}</div>
        </div>
        ${btnHtml}
      `;
      const btn = row.querySelector("button[data-act]") as HTMLButtonElement | null;
      if (btn) {
        btn.addEventListener("click", () => {
          const act = btn.dataset.act;
          if (act === "equip") {
            this.onSelectCar(car);
            this.renderShop();
            this.refreshMenuStats();
          } else if (act === "buy") {
            const ok = this.onBuyCar(car);
            if (ok) {
              this.renderShop();
              this.refreshMenuStats();
              this.showToast(`Unlocked: ${car.name}`);
            } else {
              this.showToast("Need more coins");
            }
          }
        });
      }
      this.elMenuShop.appendChild(row);
    }
  }

  private refreshMenuStats(): void {
    const d = this.storage.get();
    this.elMenuCoins.textContent = String(d.totalCoins);
    this.elMenuBest.textContent = String(d.bestScore);
    const email = this.accountEmailCb();
    const isGuest = this.accountIsGuestCb();
    if (email) {
      this.elMenuEmail.textContent = email;
      this.elMenuAccount.style.display = "";
      this.elMenuGuest.style.display = "none";
    } else if (isGuest) {
      this.elMenuAccount.style.display = "none";
      this.elMenuGuest.style.display = "";
    } else {
      this.elMenuAccount.style.display = "none";
      this.elMenuGuest.style.display = "none";
    }
    this.renderTier(d.lifetime.runs);
  }

  /**
   * Renders the fan-tier badge derived from lifetime run count. Hidden for
   * first-time players (runs === 0) so the very first menu impression isn't
   * "Rookie". Gold accent for the top tiers.
   */
  private renderTier(runs: number): void {
    if (runs <= 0) {
      this.elMenuTier.style.display = "none";
      return;
    }
    const tier = tierFor(runs);
    this.elMenuTier.textContent = tier;
    this.elMenuTier.classList.toggle(
      "tier-chip-gold",
      tier === "Gold" || tier === "Suedegod Fan",
    );
    this.elMenuTier.style.display = "";
  }

  refreshMenu(): void {
    const missions = this.refreshMissionsCb();
    this.renderMenuMissions(missions, this.missionLabelCb);
    this.renderShop();
    this.refreshMenuStats();
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!,
  );
}

/**
 * Compact DSP-icon row shown at the bottom of the menu / game-over /
 * victory screens. Each icon is a 32px circle in the brand color with a
 * white glyph; tapping opens the per-platform link in a new tab. Returns
 * empty string when no DSPs are configured so the row simply doesn't render.
 *
 * SVG paths: Spotify uses the canonical Simple Icons monochrome path
 * (MIT). Apple Music, Amazon Music, and YouTube Music use stylized
 * music-glyph SVGs so we get crisp rendering at any size without
 * shipping the official multi-color logos.
 */
function dspIconsHtml(): string {
  const list = dspIcons();
  if (list.length === 0) return "";
  const svgs: Record<string, string> = {
    spotify:
      '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.42 1.56-.301.42-1.02.599-1.56.3z"/></svg>',
    appleMusic:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 18V7l10-3v11"/><circle cx="6.5" cy="18" r="2.5" fill="currentColor" stroke="none"/><circle cx="16.5" cy="15" r="2.5" fill="currentColor" stroke="none"/></svg>',
    amazonMusic:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="11" r="3" fill="currentColor" stroke="none"/><path d="M15 11V4"/><path d="M5 18c2 2.5 4.5 3.5 7 3.5s5-1 7-3.5"/></svg>',
    youtubeMusic:
      '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="12" cy="12" r="10" fill="currentColor"/><path d="M10 8.5v7l6-3.5z" fill="#fff"/></svg>',
    instagram:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="3" width="18" height="18" rx="5"/><circle cx="12" cy="12" r="4"/><circle cx="17.5" cy="6.5" r="1.1" fill="currentColor" stroke="none"/></svg>',
  };
  const items = list
    .map((d) => {
      const safeUrl = d.url.replace(/"/g, "&quot;");
      const safeLabel = escapeHtml(d.label);
      return (
        `<a class="dsp-icon dsp-${d.key}" href="${safeUrl}" target="_blank" rel="noopener" aria-label="${safeLabel}">` +
        `${svgs[d.key] ?? ""}</a>`
      );
    })
    .join("");
  return `<div class="dsp-row">${items}</div>`;
}

/**
 * Garage swatch that matches the car's actual in-game appearance:
 * a body-colored chip with an emissive glow baked in for cars that
 * have one (Midnight Blade, K-town Neon, Suedegod Gold). Otherwise the
 * paint reads near-black and looks nothing like the bright glowing car
 * on the road.
 */
function carSwatchHtml(car: CarDef): string {
  const body = "#" + car.bodyColor.toString(16).padStart(6, "0");
  const bottomShade = "inset 0 -8px 12px rgba(0,0,0,0.35)";
  if (car.emissive == null) {
    return `<div class="car-swatch" style="background:${body};box-shadow:${bottomShade}"></div>`;
  }
  const glow = "#" + car.emissive.toString(16).padStart(6, "0");
  const bg = `radial-gradient(circle at 60% 35%, ${glow} 0%, ${body} 70%)`;
  const box = `inset 0 0 10px ${glow}, ${bottomShade}`;
  return `<div class="car-swatch" style="background:${bg};box-shadow:${box}"></div>`;
}
